import { AxiosResponse } from 'axios';
import { APIConfig, throttle } from 'src/config';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { EwasteProduct, EWasteRequestDetails, UpdateEWasteProduct, Document, UpdateEwasteStatus, RescheduleEwasteRequest, EwasteRequest } from 'src/interfaces/repair-request/e-waste-request';
import { AssigningModel } from 'src/interfaces/repair-request/repair-request';
import { wait } from 'src/utils/wait';
import axiosClient from '../utils/axios-client';

export class EWasteRequestApi {
  private requestManagementUrl = APIConfig.requestManagementUrl;

  // calls POST api to raise e-waste request in the database
  async raiseEWasteRequest(requestDetails: EwasteRequest):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}pickUpRequests`, requestDetails).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // get e-waste request details by request Id
  async getEWasteRequestById(requestId: string):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.get(`${this.requestManagementUrl}pickUpRequests/${requestId}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // add e-waste product details
  async addEWasteProduct(requestId: string, products: EwasteProduct[]):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.put(`${this.requestManagementUrl}pickUpRequests/${requestId}/products`, products).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // update e-waste product details
  async updateEWasteProduct(requestId: string, products: UpdateEWasteProduct[]):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.put(`${this.requestManagementUrl}pickUpRequests/${requestId}/product/update`, products).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // delete e-waste product details
  async deleteEWasteProduct(requestId: string, productObjectId: string):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.delete(`${this.requestManagementUrl}pickUpRequests/${requestId}/product/${productObjectId}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // update status of request
  async updateStatus(requestId: string, updateStatusDetails: UpdateEwasteStatus):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.put(`${this.requestManagementUrl}pickUpRequests/${requestId}/status`, updateStatusDetails).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async addDocument(requestId: string, documentData: Document):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.put(`${this.requestManagementUrl}pickUpRequests/${requestId}/document`, documentData).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  async deleteDocument(requestId: string, documentId: string):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.delete(`${this.requestManagementUrl}pickUpRequests/${requestId}/document/${documentId}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  async rescheduleRequest(options: RescheduleEwasteRequest):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient
        .patch(`${this.requestManagementUrl}pickUpRequests/reschedule`, options)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // calls POST api to assignChannelPartner in the database
  async assignEWasteChannelPartner(assignChannelPartner: AssigningModel):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}pickUpRequests/assign/channelPartner`, assignChannelPartner).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  // calls POST api to assign ewaste Technician in the database
  async assignEWasteTechnician(assignTechnician: AssigningModel):
  Promise<{ responseData: EWasteRequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}pickUpRequests/assign/technician`, assignTechnician).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  async getEWasteRequestsByPaginationAndFilters(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient
        .post(`${this.requestManagementUrl}pickUpRequests/getByPaginationAndFilter`, options)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getEWasteRequestsFilterData(serviceId: string): Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }
    const response = await axiosClient.get(`${this.requestManagementUrl}pickUpRequests/filters/data`, {
      params: {
        serviceId
      }
    });
    return response;
  }

  async generateEWasteReport(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient
        .post(`${this.requestManagementUrl}pickUpRequests/download/report`, options)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }
}

export const eWasteRequestApi = new EWasteRequestApi();
